import { ChevronDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { JSX } from 'react';

import { cn } from '@/lib/utils';

import { NO_DEVICE } from './prejoinState';
import type { DeviceOption } from './prejoinState';

interface DeviceSelectProps {
  /** Element id shared by the label and the select (also the select's accessible name). */
  readonly id: string;
  /** Accessible name ("Camera", "Microphone", "Speakers"); visually hidden per the mockup. */
  readonly label: string;
  /** Leading icon inside the select (Video / Mic / Volume2 in the mockup). */
  readonly icon: LucideIcon;
  readonly value: string;
  readonly options: readonly DeviceOption[];
  /** Caption of the "no device" option; omit for kinds that always have a device (speakers). */
  readonly noDeviceLabel?: string | undefined;
  readonly testid: string;
  readonly onChange: (deviceId: string) => void;
  /**
   * Honest degrade for engines without the control's backing capability (the speaker picker on
   * iOS Safari has no setSinkId): the select renders inert instead of accepting dead picks.
   */
  readonly disabled?: boolean;
  /** Extra classes on the select element (the mobile sheet grows the touch target). */
  readonly className?: string | undefined;
}

/**
 * One device picker on the prejoin screen, styled after the mockup: a compact native <select>
 * with the leading kind icon, a chevron and a visually hidden label. The native element keeps
 * the select operable from tests and e2e suites (fireEvent.change / selectOption) and free of
 * the popup portals Radix renders.
 */
export function DeviceSelect({
  id,
  label,
  icon: Icon,
  value,
  options,
  noDeviceLabel,
  testid,
  onChange,
  disabled = false,
  className,
}: DeviceSelectProps): JSX.Element {
  return (
    <label htmlFor={id} className="relative block">
      <span className="sr-only">{label}</span>
      <select
        id={id}
        data-testid={testid}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className={cn(
          'h-9 w-full cursor-pointer appearance-none rounded-lg border border-white/10 bg-white/[0.04] pr-7 pl-8 text-xs text-zinc-300 shadow-xs outline-none transition-[color,border-color,box-shadow] hover:bg-white/[0.07] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
      >
        {noDeviceLabel !== undefined ? <option value={NO_DEVICE}>{noDeviceLabel}</option> : null}
        {options.map((option) => (
          <option key={option.deviceId} value={option.deviceId}>
            {option.label}
          </option>
        ))}
      </select>
      <Icon
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500"
      />
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute top-1/2 right-2 h-3 w-3 -translate-y-1/2 text-zinc-600"
      />
    </label>
  );
}
